import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import {
  Building2, FolderKanban, TrendingUp, DollarSign,
  Activity, FileText, Wallet, BarChart3, Loader2,
} from 'lucide-react'
import Layout from '@/components/Layout'
import { dashboardApi } from '@/api/dashboard'
import { useAuthStore } from '@/stores/authStore'
import { BRAND } from '@/config/brand'

const STATUS_COLORS: Record<string, string> = {
  planning: '#FBBF24',
  tendering: '#A78BFA',
  awarded: '#60A5FA',
  active: '#10B981',
  on_hold: '#9CA3AF',
  completed: '#3B82F6',
  cancelled: '#EF4444',
}

const TYPE_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
  '#84CC16', '#6366F1', '#14B8A6',
]

export default function DashboardPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const isArabic = i18n.language === 'ar'

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.getStats(),
  })

  const formatNumber = (value: number, decimals = 0): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  }

  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`
    }
    return value.toFixed(0)
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
        </div>
      </Layout>
    )
  }

  const summary = data?.summary
  const statusData = data?.projects_by_status?.map((item) => ({
    name: t(`projects.statuses.${item.status}`),
    value: item.count,
    color: STATUS_COLORS[item.status] || '#9CA3AF',
  })) || []

  const typeData = data?.projects_by_type?.map((item, idx) => ({
    name: t(`projects.types.${item.type}`),
    count: item.count,
    value: item.total_value,
    color: TYPE_COLORS[idx % TYPE_COLORS.length],
  })) || []

  const progressData = data?.projects_progress || []

  return (
    <Layout>
      <div className="p-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('dashboard.welcome')}, {user?.full_name || 'User'} 👋
          </h2>
          <p className="text-gray-600">{t('dashboard.overview')}</p>
          <p className="text-sm text-blue-600 font-medium mt-1">
            {user?.tenant?.company_name_en || BRAND.name}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<Building2 className="w-6 h-6" />}
            label={t('dashboard.totalCompanies')}
            value={summary?.total_companies || 0}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard
            icon={<FolderKanban className="w-6 h-6" />}
            label={t('dashboard.totalProjects')}
            value={summary?.total_projects || 0}
            color="bg-purple-100 text-purple-600"
            onClick={() => navigate('/projects')}
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label={t('dashboard.activeProjects')}
            value={summary?.active_projects || 0}
            color="bg-green-100 text-green-600"
          />
          <StatCard
            icon={<DollarSign className="w-6 h-6" />}
            label={`${t('dashboard.contractValue')} (AED)`}
            value={formatCurrency(summary?.total_contract_value || 0)}
            color="bg-yellow-100 text-yellow-600"
            isText
          />
        </div>

        {/* Financial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <FinancialCard
            icon={<DollarSign className="w-5 h-5" />}
            label={t('dashboard.contractValue')}
            value={summary?.total_contract_value || 0}
            gradient="from-blue-500 to-blue-600"
          />
          <FinancialCard
            icon={<Activity className="w-5 h-5" />}
            label={t('dashboard.actualCost')}
            value={summary?.total_actual_cost || 0}
            gradient="from-orange-500 to-orange-600"
          />
          <FinancialCard
            icon={<Wallet className="w-5 h-5" />}
            label={t('dashboard.paidAmount')}
            value={summary?.total_paid_amount || 0}
            gradient="from-green-500 to-green-600"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Pie Chart - Projects by Status */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-bold text-gray-900">
                {t('dashboard.projectsByStatus')}
              </h3>
            </div>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                {t('dashboard.noProjects')}
              </div>
            )}
          </div>

          {/* Bar Chart - Projects by Type */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-bold text-gray-900">
                {t('dashboard.projectsByType')}
              </h3>
            </div>
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Count" radius={[8, 8, 0, 0]}>
                    {typeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                {t('dashboard.noProjects')}
              </div>
            )}
          </div>
        </div>

        {/* Projects Progress */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-bold text-gray-900">
              {t('dashboard.projectsProgress')}
            </h3>
          </div>
          {progressData.length > 0 ? (
            <div className="space-y-4">
              {progressData.map((project) => {
                const displayName = isArabic && project.name_ar ? project.name_ar : project.name
                return (
                  <div
                    key={project.id}
                    className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">{project.code}</p>
                        <p className="font-medium text-gray-900">{displayName}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-sm font-bold text-gray-900">
                          {project.progress.toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatNumber(project.contract_value)} {project.currency}
                        </p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(project.progress, 100)}%`,
                          background: project.progress >= 80
                            ? 'linear-gradient(90deg, #10B981, #059669)'
                            : project.progress >= 50
                              ? 'linear-gradient(90deg, #3B82F6, #2563EB)'
                              : 'linear-gradient(90deg, #F59E0B, #D97706)',
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              {t('dashboard.noProjects')}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: number | string
  color: string
  onClick?: () => void
  isText?: boolean
}

function StatCard({ icon, label, value, color, onClick, isText }: StatCardProps) {
  return (
    <div
      className={`card hover:shadow-lg transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-3xl font-bold text-gray-900">
        {isText ? value : (typeof value === 'number' ? value.toLocaleString() : value)}
      </p>
    </div>
  )
}

interface FinancialCardProps {
  icon: React.ReactNode
  label: string
  value: number
  gradient: string
}

function FinancialCard({ icon, label, value, gradient }: FinancialCardProps) {
  const formatNumber = (val: number): string => {
    return val.toLocaleString('en-US', { maximumFractionDigits: 0 })
  }

  return (
    <div className={`card bg-gradient-to-br ${gradient} text-white relative overflow-hidden`}>
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2 opacity-90">
          {icon}
          <p className="text-sm">{label}</p>
        </div>
        <p className="text-3xl font-bold">{formatNumber(value)}</p>
        <p className="text-sm opacity-75 mt-1">AED</p>
      </div>
      <div className="absolute -right-4 -bottom-4 opacity-10">
        <DollarSign className="w-32 h-32" />
      </div>
    </div>
  )
}